// Las operaciones de Tiendanube, fuera de las pantallas — ADR-2026-09-18-las-operaciones-de-tiendanube-salen-de-las-pantallas.
//
// `lib/tiendaNube.ts` tiene las llamadas sueltas (traer, importar, exportar). Lo que
// faltaba era la capa de ARRIBA: partir en tandas, armar el pedido de exportación,
// esperar a que el worker marque las fotos. Eso vivía adentro de dos componentes React
// (`ExportarTiendanube.tsx` y `AgregarVariosModal.tsx`), así que no se podía llamar
// desde ningún otro lado — en particular, desde una herramienta del agente.
//
// Acá no hay React ni nada de la pantalla: lo que necesita red se INYECTA, así cada
// pieza se prueba sola. Las pantallas siguen haciendo exactamente lo mismo que antes;
// lo único que cambia es desde dónde lo llaman.
import {
  fireTnExport,
  fireTnImport,
  type TnExportProducto,
  type TnProduct,
} from './tiendaNube';

/**
 * Cuántos productos entran en cada llamada al importador. El backend encola cada tanda
 * y la procesa con un worker, así que el número no es un límite suyo sino el tamaño de
 * bocado que le damos: 50 es el tope que valida el endpoint, 25 deja margen.
 */
export const TN_BATCH = 25;

/** Parte una lista en tandas del tamaño pedido. */
export function enTandas<T>(items: T[], tamano: number = TN_BATCH): T[][] {
  if (tamano < 1) return items.length > 0 ? [items] : [];
  const tandas: T[][] = [];
  for (let i = 0; i < items.length; i += tamano) tandas.push(items.slice(i, i + tamano));
  return tandas;
}

/** A dónde van los productos importados. */
export interface DestinoDeImport {
  userId: string | undefined;
  companyId: string | null;
  brandId: string | null;
}

/**
 * Manda a importar los productos de la tienda, en tandas.
 *
 * **Es fire-and-forget:** el backend encola y contesta al instante, así que cuando esto
 * termina los productos todavía no existen en Atlas — van apareciendo a medida que el
 * worker los crea. Quien llame no puede decir "listo", sólo "los estoy trayendo".
 *
 * Devuelve cuántos se mandaron de verdad. Importa que sea "de verdad" y no "los que se
 * pidieron": las tandas salen una atrás de otra desde el navegador, así que si algo
 * corta en el medio, la cuenta honesta es la de las que llegaron a salir.
 *
 * "Salir" es que el backend la ACEPTÓ. La primera tanda rechazada corta las que
 * siguen: lo que la rechazó (sesión vencida, backend caído) va a rechazar las demás, y
 * así `enviados` son siempre los PRIMEROS `enviados` de la lista, que es lo que asume
 * el chat al nombrarlos (review Greptile #154).
 */
export async function importarProductosDeLaTienda(
  productos: TnProduct[],
  destino: DestinoDeImport,
): Promise<{ enviados: number; tandas: number; motivo?: string }> {
  const payload = productos.map((t) => ({
    nombre: t.nombre,
    imgs: t.imgs,
    tn_product_id: t.id,
    variantes: t.variantes,
  }));
  const tandas = enTandas(payload);
  let enviados = 0;
  for (const tanda of tandas) {
    const disparo = await fireTnImport({ ...destino, productos: tanda });
    if (!disparo.ok) return { enviados, tandas: tandas.length, motivo: disparo.motivo };
    enviados += tanda.length;
  }
  return { enviados, tandas: tandas.length };
}

// ── Traer el catálogo entero al conectar ────────────────────────────────────

export interface DepsDelCatalogo {
  /** Los productos de la tienda que todavía no están en Atlas (`tn-products` ya descuenta los importados). */
  pendientes: () => Promise<{ conectada: boolean; productos: TnProduct[] }>;
  importar: (productos: TnProduct[]) => Promise<{ enviados: number }>;
  /** Para los tests: reemplaza la pausa entre reintentos. */
  dormir?: (ms: number) => Promise<void>;
}

/**
 * Trae a Atlas TODO lo que la tienda tiene y Atlas no — lo que pasa al conectar.
 *
 * Es la regla del camino que arranca en Tiendanube (`TiendanubeConectar`): conectar
 * trae el catálogo. ADR-2026-10-01-conectar-desde-atlas-trae-el-catalogo la extiende a
 * las puertas que conectan desde adentro de Atlas.
 *
 * Justo después de vincular, `tn-products` a veces contesta cero aunque la tienda tenga
 * productos (la consulta a Tiendanube pega demasiado rápido). Por eso un cero se
 * reintenta dos veces antes de creerlo — mismo criterio que el install-first.
 *
 * Devuelve cuántos encontró, cuántos aceptó el backend y CUÁLES quedaron sin mandar.
 * "Cero mandados" puede ser "no había nada" o "falló", y quien le cuenta a una persona
 * tiene que poder distinguirlos. Los que quedaron afuera vuelven como lista para
 * reintentar sólo esos: volver a leer la tienda listaría también los que siguen en la
 * cola del worker, que no deduplica, y se duplicarían (review Greptile #154).
 * Fire-and-forget como el import: todavía no existen en Atlas cuando esto vuelve.
 */
export async function traerElCatalogoDeLaTienda(
  deps: DepsDelCatalogo,
): Promise<{ encontrados: number; enviados: number; sinMandar: TnProduct[] }> {
  const dormir = deps.dormir ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  let res = await deps.pendientes();
  for (let intento = 0; res.conectada && res.productos.length === 0 && intento < 2; intento++) {
    await dormir(1500);
    res = await deps.pendientes();
  }
  if (!res.conectada || res.productos.length === 0) {
    return { encontrados: 0, enviados: 0, sinMandar: [] };
  }
  const { enviados } = await deps.importar(res.productos);
  // `importarProductosDeLaTienda` corta en la primera tanda rechazada, así que los que
  // salieron son siempre los primeros `enviados`.
  return {
    encontrados: res.productos.length,
    enviados,
    sinMandar: res.productos.slice(enviados),
  };
}

// ── Exportar fotos a la tienda ──────────────────────────────────────────────

/**
 * Qué se hace con un producto al exportar. Son TRES, no cuatro: "mandar las fotos a
 * otro producto" es `agregar` apuntando a otro `tnProductId`, y las pantallas ya lo
 * traducían así antes de llamar al backend.
 *
 * `reemplazar` **borra todas las fotos que el producto tiene hoy en la tienda** y sube
 * las nuevas. No se deshace desde Atlas.
 */
export type AccionDeExport =
  | { tipo: 'agregar'; tnProductId: number }
  | { tipo: 'reemplazar'; tnProductId: number }
  | {
      tipo: 'crear';
      nuevo: {
        nombre: string;
        precio: string;
        descripcion: string;
        stock: string;
        sku: string;
      };
    };

/** Una imagen generada de Atlas, lista para mandar. */
export interface ImagenParaExportar {
  /** `imagenes_generadas.id` — con esto el worker marca `tn_exported_at`. */
  id: string;
  /** El nombre del archivo en Storage; se firma antes de mandarlo. */
  fileName: string;
  /** Posición en la galería del producto. */
  orden: number;
}

export interface ProductoParaExportar {
  /** `productos.id` de Atlas. Viaja siempre: el worker lo usa para guardar el vínculo. */
  productoId: string;
  accion: AccionDeExport;
  imagenes: ImagenParaExportar[];
}

/** Firma el original de una imagen para que Tiendanube pueda bajarlo. `null` = no se pudo. */
export type FirmarImagen = (fileName: string) => Promise<string | null>;

/**
 * Arma el pedido que entiende el backend.
 *
 * Las imágenes que no se pudieron firmar **se descartan en silencio**: mandar una URL
 * vacía haría fallar el producto entero en el worker, y perder una foto de varias es
 * mejor que perderlas todas. Quien llame puede comparar cuántas pidió con cuántas
 * quedaron para avisarle a la persona.
 */
export async function construirExportDeProductos(
  productos: ProductoParaExportar[],
  firmar: FirmarImagen,
): Promise<TnExportProducto[]> {
  return Promise.all(
    productos.map(async (p) => {
      const imagenes = await Promise.all(
        p.imagenes.map(async (i) => ({
          imagen_generada_id: i.id,
          src: (await firmar(i.fileName)) ?? '',
          orden: i.orden,
        })),
      );
      const accion = p.accion;
      const action =
        accion.tipo === 'crear' ? 'create' : accion.tipo === 'reemplazar' ? 'replace' : 'add';
      const salida: TnExportProducto = {
        producto_id: p.productoId,
        action,
        tn_product_id: accion.tipo === 'crear' ? null : accion.tnProductId,
        imagenes: imagenes.filter((x) => x.src),
      };
      if (accion.tipo === 'crear') salida.nuevo = accion.nuevo;
      return salida;
    }),
  );
}

/**
 * Arma el pedido y lo manda. Es el camino completo, el que usan las pantallas y el que
 * va a usar el agente.
 *
 * Igual que el import, **es fire-and-forget**: el backend encola y contesta. La señal de
 * que una foto llegó de verdad es `imagenes_generadas.tn_exported_at`, que el worker
 * escribe sólo si Tiendanube devolvió un id de imagen.
 */
export async function exportarFotosALaTienda(args: {
  productos: ProductoParaExportar[];
  firmar: FirmarImagen;
  userId: string | undefined;
  companyId: string;
  brandId: string | null;
}): Promise<{
  productos: number;
  imagenes: number;
  /**
   * Las fotos que VIAJARON de verdad, por id.
   *
   * No alcanza con el número: las que no se pudieron firmar se descartan en silencio (ver
   * `construirExportDeProductos`), y quien llama necesita saber CUÁLES quedaron afuera,
   * no cuántas. Sin esto, el chat marcaba como subidas también a las descartadas y un
   * reintento contestaba "esa ya está" por una foto que nunca salió
   * (review Greptile #127).
   */
  imagenIds: string[];
  motivo?: string;
}> {
  const payload = await construirExportDeProductos(args.productos, args.firmar);
  const disparo = await fireTnExport({
    userId: args.userId,
    companyId: args.companyId,
    brandId: args.brandId,
    productos: payload,
  });
  // Si el backend no lo aceptó, NO salió nada: se devuelve cero con el motivo, para que
  // quien tenga que contárselo a una persona no diga "la estoy subiendo" de gusto.
  if (!disparo.ok) return { productos: 0, imagenes: 0, imagenIds: [], motivo: disparo.motivo };
  // Desde la galería todas son generadas; una subida (sólo en lanzamientos) no tiene fila que mirar.
  const imagenIds = payload.flatMap((p) => p.imagenes.map((i) => i.imagen_generada_id).filter((x): x is string => !!x));
  return { productos: payload.length, imagenes: imagenIds.length, imagenIds };
}

// ── Esperar a que el worker las marque ──────────────────────────────────────

/** Cuántas de esas imágenes ya tienen `tn_exported_at`. */
export type ContarExportadas = (ids: string[]) => Promise<number>;

export interface OpcionesDeEspera {
  /** Cuánto esperar entre consultas. */
  cadaMs?: number;
  /** Cuánto esperar en total antes de rendirse. */
  hastaMs?: number;
  onAvance?: (listas: number, total: number) => void;
  /** Para los tests: reemplaza el reloj y la pausa. */
  ahora?: () => number;
  dormir?: (ms: number) => Promise<void>;
}

/**
 * Espera a que TODAS las imágenes queden marcadas como subidas, o se rinde.
 *
 * Rendirse **no significa que fallaron**: el worker sigue trabajando y las va a marcar
 * igual. Por eso devuelve cuántas llegó a ver listas, y quien llame decide qué contar.
 * El tope por defecto sale de lo medido: Tiendanube baja la foto en alta, así que son
 * unos dos segundos por imagen, y el export admite hasta cincuenta.
 */
export async function esperarExportadas(
  ids: string[],
  contar: ContarExportadas,
  opciones: OpcionesDeEspera = {},
): Promise<number> {
  const {
    cadaMs = 2500,
    hastaMs = 130000,
    onAvance,
    ahora = () => Date.now(),
    dormir = (ms: number) => new Promise<void>((r) => setTimeout(r, ms)),
  } = opciones;
  if (ids.length === 0) return 0;
  const limite = ahora() + hastaMs;
  let listas = 0;
  while (ahora() < limite) {
    listas = await contar(ids);
    onAvance?.(listas, ids.length);
    if (listas >= ids.length) break;
    await dormir(cadaMs);
  }
  return listas;
}
